import { UploadOutlined } from "@mui/icons-material";
import { Box, Button, DialogActions, Divider, FormControl, InputLabel, MenuItem, Paper, Select, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { menuPropsModal } from "../../../utils";
import { DataSelect } from "../../common/DataSelect/DataSelect";
import { formatArrayInputValue, getValueByPath, setValueByPath } from "../helpers";
import { IFieldDefinition, IFormState } from "../interfaces";

interface IRecordFormModalProps {
  title: string;
  fields?: IFieldDefinition<any>[];
  initialFormState: IFormState;
  onSaved?: (values: IFormState) => void | Promise<void>;
  isSaving?: boolean;
  resetKey?: string | number;
}

export const RecordFormModal = (props: IRecordFormModalProps) => {
  const { title, fields, initialFormState, onSaved, isSaving, resetKey } = props;

  const [formState, setFormState] = useState<IFormState>(() => initialFormState);

  useEffect(() => {
    setFormState(initialFormState);
  }, [resetKey, initialFormState]);

  const handleChange = (fieldPath: string, value: unknown) => {
    setFormState((current) => setValueByPath(current, fieldPath, value));
  };

  const imageFieldPath = useMemo(() => {
    const findImageField = (items?: IFieldDefinition<any>[], parentPath = ""): string | null => {
      for (const field of items ?? []) {
        const fieldKey = String(field.key);
        const currentPath = parentPath ? `${parentPath}.${fieldKey}` : fieldKey;

        if (field.type === "image") {
          return currentPath;
        }

        if (field.type === "object") {
          const nestedMatch = findImageField(field.fields, currentPath);

          if (nestedMatch) {
            return nestedMatch;
          }
        }
      }

      return null;
    };

    return findImageField(fields);
  }, [fields]);

  const imagePreview = imageFieldPath ? String(getValueByPath(formState, imageFieldPath) ?? "") : "";

  const hasSelectedImage = Boolean(imagePreview);

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file || !imageFieldPath) {
      return;
    }

    const localPreviewUrl = URL.createObjectURL(file);

    setFormState((current) => {
      let nextState = setValueByPath(current, imageFieldPath, localPreviewUrl);
      nextState = setValueByPath(nextState, "__imageFile", file);
      nextState = setValueByPath(nextState, "__imageFileName", file.name);
      return nextState;
    });
  };

  const renderField = (field: IFieldDefinition<any>, parentPath = ""): React.ReactNode => {
    const fieldKey = String(field.key);
    const fieldPath = parentPath ? `${parentPath}.${fieldKey}` : fieldKey;
    const currentValue = getValueByPath(formState, fieldPath);
    const isDescriptionField = fieldPath === "description" || fieldKey === "description";

    if (field.type === "object") {
      return (
        <Paper
          key={fieldPath}
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 3,
            bgcolor: "grey.50",
          }}
        >
          <Stack spacing={2}>
            <Typography variant="subtitle2" fontWeight={700}>
              {field.label}
            </Typography>

            {(field.fields ?? []).map((nestedField) => renderField(nestedField, fieldPath))}
          </Stack>
        </Paper>
      );
    }

    if (field.type === "image") {
      return (
        <Paper
          key={fieldPath}
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 3,
            bgcolor: "grey.50",
          }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>
                {field.label}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Upload an image to preview it in the record.
              </Typography>
            </Box>

            <Button variant="outlined" component="label" startIcon={<UploadOutlined />} sx={{ alignSelf: "flex-start" }}>
              {hasSelectedImage ? "Change image" : "Select image"}
              <input hidden accept="image/*" type="file" onChange={handleImageSelect} />
            </Button>

            {hasSelectedImage ? (
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 3,
                  bgcolor: "background.paper",
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src={imagePreview}
                  alt="Preview"
                  sx={{
                    width: "100%",
                    height: 220,
                    objectFit: "contain",
                    display: "block",
                    bgcolor: "grey.100",
                  }}
                />
              </Box>
            ) : null}
          </Stack>
        </Paper>
      );
    }

    if (field.type === "select") {
      return (
        <FormControl key={fieldPath} fullWidth required={field.required}>
          <InputLabel>{field.label}</InputLabel>

          <Select
            label={field.label}
            value={String(currentValue ?? "")}
            onChange={(event) => handleChange(fieldPath, event.target.value)}
            MenuProps={menuPropsModal}
          >
            {(field.options ?? []).map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (field.type === "relation") {
      return (
        <DataSelect
          key={fieldPath}
          label={field.label}
          value={(currentValue as string | null) ?? null}
          source={field.relation.source}
          placeholder={field.placeholder}
          onChange={(nextValue) => {
            handleChange(fieldPath, nextValue);
          }}
        />
      );
    }

    if (field.type === "array") {
      return (
        <TextField
          fullWidth
          label={field.label}
          placeholder={field.placeholder}
          value={
            typeof formState[String(field.key)] === "string" ? formState[String(field.key)] : formatArrayInputValue(formState[String(field.key)])
          }
          onChange={(event) => {
            handleChange(String(field.key), event.target.value);
          }}
        />
      );
    }

    return (
      <TextField
        key={fieldPath}
        fullWidth
        label={field.label}
        placeholder={field.placeholder}
        value={String(currentValue ?? "")}
        onChange={(event) =>
          handleChange(fieldPath, field.type === "number" ? (event.target.value === "" ? "" : Number(event.target.value)) : event.target.value)
        }
        multiline={isDescriptionField}
        minRows={isDescriptionField ? 4 : undefined}
        required={field.required}
        type={field.type === "number" ? "number" : "text"}
      />
    );
  };

  const handleSave = () => {
    onSaved?.(formState);
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 680,
        minWidth: 0,
        px: 3,
        py: 3,
      }}
    >
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {title}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            Complete the information below.
          </Typography>
        </Box>

        <Divider />

        <Box
          sx={{
            pr: 1,
          }}
        >
          <Stack spacing={2.5}>{(fields ?? []).map((field) => renderField(field))}</Stack>
        </Box>

        <DialogActions sx={{ px: 0, pt: 0 }}>
          <Button variant="contained" onClick={handleSave} sx={{ mt: 1 }} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Stack>
    </Box>
  );
};
